import { useState } from 'react'
import { FiInfo } from 'react-icons/fi'
import { faFull } from 'utilities/helper/format'
import { faCompact } from 'pages/fund/adminDashboard/components/chartTheme'

/**
 * سرمایه‌اش در طول زمان.
 *
 * Hand-drawn SVG rather than recharts: this is one monotone-ish line with a
 * few markers, and pulling recharts in would put it in the main bundle for a
 * page most members' managers open a handful of times a day. The admin
 * dashboard lazy-loads recharts precisely to keep it out.
 *
 * The last point equals the سرمایه number on the card above, because the
 * backend builds both from the same cash-in/cash-out sets. Two different
 * numbers for «سرمایه» on one screen would make both untrustworthy.
 */

const W = 330
const H = 124
const BASE = 100
const TOP = 22

function InfoDot({ onClick }: { onClick: () => void }) {
    // The hollow (i) the dashboard's Hint and the grant wizard's CandidateRow
    // use. A filled green disc beside a heading reads as a status badge — «this
    // chart is fine» — rather than as «tap to read what it means». Click, never
    // hover: the user dislikes hover affordances and they do not exist on a
    // phone anyway.
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="توضیح نمودار سرمایه"
            className=" -my-3 flex h-11 w-11 items-center justify-center text-text-muted"
        >
            <FiInfo aria-hidden size={16} />
        </button>
    )
}

export default function CapitalChart({
    series,
    title = 'سرمایه‌اش در طول زمان',
}: {
    series: IMemberCapitalSeries
    /** second person when the member is reading their own record (pwa#61) */
    title?: string
}) {
    const [hintOpen, setHintOpen] = useState(false)
    const points = series.points

    if (points.length < 2) {
        return (
            <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
                <p className=" text-sm font-bold text-text-body">
                    {title}
                </p>
                <p className=" mt-3 text-xs text-text-secondary">
                    هنوز آن‌قدر واریز و برداشت ثبت نشده که نموداری از آن دربیاید.
                </p>
            </section>
        )
    }

    const values = points.map((p) => p.value)
    const max = Math.max(...values, 1)
    const min = Math.min(...values, 0)
    const span = max - min || 1
    const x = (index: number) =>
        6 + (index * (W - 12)) / Math.max(points.length - 1, 1)
    const y = (value: number) =>
        BASE - ((value - min) / span) * (BASE - TOP)

    const line = points
        .map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
        .join(' ')
    const area = `${line} L${x(points.length - 1).toFixed(1)},${BASE} L6,${BASE} Z`

    // markers land on the point whose date is closest to the loan's, so a
    // thinned series still puts them in the right place along the axis
    const marks = series.loan_marks.map((mark) => {
        let best = 0
        for (let i = 0; i < points.length; i += 1) {
            if (points[i].date <= mark.date) best = i
        }
        return { ...mark, x: x(best) }
    })

    // one tick per Jalali year, but only while they stay far enough apart to
    // read: a member with years of history packs the last few together and
    // «۱۴۰۴» and «۱۴۰۵» printed straight into each other
    const MIN_TICK_GAP = 34
    const yearTicks: { x: number; label: string }[] = []
    let lastYear = ''
    points.forEach((point, index) => {
        const year = point.date.slice(0, 4)
        if (year === lastYear) return
        lastYear = year
        const at = x(index)
        const previous = yearTicks[yearTicks.length - 1]
        if (previous && at - previous.x < MIN_TICK_GAP) return
        yearTicks.push({ x: at, label: year })
    })

    return (
        <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
            <div className=" relative flex items-center gap-1.5">
                <p className=" text-sm font-bold text-text-body">
                    {title}
                </p>
                <InfoDot onClick={() => setHintOpen((open) => !open)} />
                {hintOpen && (
                    <div
                        onClick={() => setHintOpen(false)}
                        className=" absolute right-0 top-full z-30 mt-1.5 max-w-[270px] rounded-md bg-brand px-3 py-2.5 text-[11px] leading-5 text-white shadow-pop"
                    >
                        جزء «تأمین سرمایه» در امتیاز عضو، دقیقاً مساحت زیر همین
                        نمودار است — چند تومان، چند روز.
                    </div>
                )}
            </div>

            {/* charts are dir=ltr islands so time runs left-to-right, the same
                convention as the dashboard's monthly charts */}
            <div dir="ltr" className=" mt-3">
                <svg viewBox={`0 0 ${W} ${H}`} className=" block h-auto w-full">
                    {[TOP, (TOP + BASE) / 2, BASE].map((gy) => (
                        <line
                            key={gy}
                            x1="6"
                            y1={gy}
                            x2={W - 6}
                            y2={gy}
                            stroke="#F0F2F5"
                            strokeWidth="1"
                        />
                    ))}
                    {/* ONE y label, on the top gridline: the baseline is zero
                        (min is clamped to 0 above), so the top of the scale
                        plus the unit is the whole axis. A full set of ticks
                        would say the same thing four times on a 124px-tall
                        chart. Top-LEFT because time runs left-to-right here,
                        so a rising capital curve leaves that corner empty.
                        RLE…PDF: this sits inside the dir="ltr" island, which
                        would otherwise flip «۳۰۰ میلیون تومان» digits-first. */}
                    <text
                        x="8"
                        y={TOP - 4}
                        fontSize="9"
                        fill="#A7A7A7"
                        textAnchor="start"
                    >
                        {`\u202B${faCompact.format(max)} تومان\u202C`}
                    </text>
                    <path d={area} fill="#0A93A6" fillOpacity="0.13" />
                    <path
                        d={line}
                        fill="none"
                        stroke="#0A93A6"
                        strokeWidth="2"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                    {marks.map((mark, index) => (
                        <g key={index}>
                            <line
                                x1={mark.x}
                                y1={TOP}
                                x2={mark.x}
                                y2={BASE}
                                stroke="#5A9E2F"
                                strokeWidth="1"
                                strokeDasharray="3 3"
                            />
                            <circle cx={mark.x} cy={BASE} r="3.5" fill="#5A9E2F" />
                        </g>
                    ))}
                    {yearTicks.map((tick) => (
                        <text
                            key={tick.label}
                            x={Math.min(Math.max(tick.x, 16), W - 16)}
                            y={H - 8}
                            fontSize="9"
                            fill="#A7A7A7"
                            textAnchor="middle"
                        >
                            {tick.label}
                        </text>
                    ))}
                </svg>
            </div>

            <div className=" mt-2 flex gap-3.5">
                <span className=" flex items-center gap-1.5 text-[11px] text-text-secondary">
                    <span className=" h-[3px] w-2.5 rounded-sm bg-[#0A93A6]" />
                    سرمایه
                </span>
                {marks.length > 0 && (
                    <span className=" flex items-center gap-1.5 text-[11px] text-text-secondary">
                        <span className=" h-2 w-2 rounded-full bg-[#5A9E2F]" />
                        زمان دریافت وام
                    </span>
                )}
            </div>
            <p className=" mt-2 text-[11px] text-text-secondary">
                امروز: {faFull.format(series.current)} تومان
            </p>
        </section>
    )
}
